// components/create/PollsSection.js
//
// Polls on the invitation — "what should we order", "which nominee" — which
// guests answer from the invitation page.
//
// The website passed `polls` and `setPolls` all the way down to BasicSection
// and then never rendered a control for them, so the array was always empty
// and the column always null. Same shape as the app writes:
//
//   { id: string, question: string, options: string[] }
//
// including the suggested poll it offers for Freshers and Farewell parties,
// because those are the two event types where a poll is the point.

import React, { useState } from 'react';
import { FiTrash2, FiPlus, FiX } from 'react-icons/fi';
import '../../styles/PollsSection.scss';
import Icon from '../Icon';

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 8;

const emptyDraft = () => ({ question: '', options: ['', ''] });

export default function PollsSection({ polls = [], setPolls, eventType }) {
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');

  const suggestion =
    eventType === 'Freshers Party' || eventType === 'Farewell Party'
      ? `Vote for Mr & Ms ${eventType === 'Freshers Party' ? 'Fresher' : 'Farewell'}`
      : null;

  const addSuggested = () => {
    setPolls([
      ...polls,
      {
        id: Date.now().toString(),
        question: suggestion,
        options: ['Nominee 1', 'Nominee 2', 'Nominee 3', 'Nominee 4'],
      },
    ]);
  };

  const setOption = (index, value) => {
    setDraft((d) => ({
      ...d,
      options: d.options.map((o, i) => (i === index ? value : o)),
    }));
  };

  const save = () => {
    const question = draft.question.trim();
    // Blank options are dropped rather than rejected: someone who added a
    // fourth row and left it empty meant three options, not a mistake.
    const options = draft.options.map((o) => o.trim()).filter(Boolean);

    if (!question) { setError('Give the poll a question.'); return; }
    if (options.length < MIN_OPTIONS) { setError('A poll needs at least two options.'); return; }
    if (new Set(options.map((o) => o.toLowerCase())).size !== options.length) {
      setError('Two options are the same.');
      return;
    }

    setPolls([...polls, { id: Date.now().toString(), question, options }]);
    setDraft(null);
    setError('');
  };

  return (
    <div className="section-card">
      <label className="section-title">Polls</label>
      <p className="section-description">
        Ask your guests something. They answer on the invitation.
      </p>

      {polls.length > 0 && (
        <ul className="poll-list">
          {polls.map((poll) => (
            <li key={poll.id}>
              <span className="poll-text">
                <strong>{poll.question}</strong>
                <small>
                  {poll.options.slice(0, 2).join(' · ')}
                  {poll.options.length > 2 && ` +${poll.options.length - 2} more`}
                </small>
              </span>
              <button
                type="button"
                className="poll-remove"
                onClick={() => setPolls(polls.filter((p) => p.id !== poll.id))}
                aria-label={`Remove poll: ${poll.question}`}
              >
                <FiTrash2 />
              </button>
            </li>
          ))}
        </ul>
      )}

      {suggestion && !polls.some((p) => p.question === suggestion) && (
        <button type="button" className="poll-suggestion" onClick={addSuggested}>
          <span aria-hidden="true">💡</span>
          <span className="poll-text">
            <strong>Suggested poll</strong>
            <small>{suggestion}</small>
          </span>
          <Icon name="add-circle" size={20} />
        </button>
      )}

      {draft ? (
        <div className="poll-draft">
          <label className="field-label" htmlFor="poll-question">Question</label>
          <input
            id="poll-question"
            type="text"
            className="text-input"
            maxLength={120}
            placeholder="What should we order?"
            value={draft.question}
            onChange={(e) => { setDraft({ ...draft, question: e.target.value }); setError(''); }}
            autoFocus
          />

          <label className="field-label">Options</label>
          {draft.options.map((option, index) => (
            <div className="poll-option-row" key={index}>
              <input
                type="text"
                className="text-input"
                maxLength={60}
                placeholder={`Option ${index + 1}`}
                value={option}
                onChange={(e) => { setOption(index, e.target.value); setError(''); }}
              />
              {draft.options.length > MIN_OPTIONS && (
                <button
                  type="button"
                  className="poll-remove"
                  onClick={() => setDraft({
                    ...draft,
                    options: draft.options.filter((_, i) => i !== index),
                  })}
                  aria-label={`Remove option ${index + 1}`}
                >
                  <FiX />
                </button>
              )}
            </div>
          ))}

          {draft.options.length < MAX_OPTIONS && (
            <button
              type="button"
              className="poll-add-option"
              onClick={() => setDraft({ ...draft, options: [...draft.options, ''] })}
            >
              <FiPlus />
              Add option
            </button>
          )}

          {error && <p className="poll-error">{error}</p>}

          <div className="poll-draft-actions">
            <button type="button" className="btn btn-secondary" onClick={() => { setDraft(null); setError(''); }}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={save}>
              Add poll
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="add-button" onClick={() => setDraft(emptyDraft())}>
          <Icon name="add-circle" />
          <span>Add a poll</span>
        </button>
      )}
    </div>
  );
}
